import { useEffect, useRef } from 'react';

import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSuperState } from 'hooks';

export type PreviewQuestion = {
    Name: string;
    ObjectiveName: string;
    QuestionId: number;
    Sources: string;
    TopicName: string;
    TypeName: string;
};

type IncludeControl = {
    isIncluded: (question: PreviewQuestion) => boolean;
    onToggle: (question: PreviewQuestion) => void;
};

type QuestionPreviewModalProps = {
    cultureId: number;
    includeControl?: IncludeControl;
    index: number;
    onHide: () => void;
    onNavigate: (index: number) => void;
    questions: PreviewQuestion[];
    show: boolean;
    studentId: number;
    teacherId: number;
};

// Maps the admin host to the matching student-site host (mirrors the reports
// QuestionViewer's in-app mode): dev/stage manage hosts preview against their
// student-site counterparts, localhost previews against dev, otherwise prod.
const getViewerBaseUrl = (): string => {

    const host = window.location.host.toLowerCase();

    if (host.includes('devmanage')) return 'dev.gmetrix.net';
    if (host.includes('stagemanage')) return 'stage.gmetrix.net';
    if (host.includes('localhost')) return 'dev.gmetrix.net';

    return 'gmetrix.net';

};

/**
 * The Question Preview modal from the Custom Tests 2.0 mock: the question
 * renders in an iframe on the left with a metadata rail (QID / Objective /
 * Question Type / Source / Topic) on the right, and Previous/Next footer
 * navigation. The wizard passes includeControl to add an Include toggle so a
 * question can be added to / removed from the test without leaving the modal.
 *
 * The student site's viewer authorizes teacher previews through a real
 * teacher -> student relationship, so callers must resolve a partner student
 * id first (an empty student id fails to load).
 */
export const QuestionPreviewModal = (props: QuestionPreviewModalProps) => {

    const { cultureId, includeControl, index, onHide, onNavigate, questions, show, studentId, teacherId } = props;

    const { formatMessage } = useIntl();
    const loadTimeout = useRef<number | null>(null);
    const [state, setState] = useSuperState({
        iframeLoading: true
    });
    const { iframeLoading } = state;

    const question = questions[index];

    // Show the loader again whenever a different question loads into the iframe.
    useEffect(() => {

        setState({ iframeLoading: true });

    }, [question?.QuestionId, show]);

    useEffect(() => () => {

        if (loadTimeout.current) clearTimeout(loadTimeout.current);

    }, []);

    const baseUrl = getViewerBaseUrl();
    const included = includeControl && question ? includeControl.isIncluded(question) : false;

    const renderMeta = (label: string, value: string) => (
        <div className={'d-flex flex-column'}>
            <span className={'text-muted small'}>
                {label}
            </span>
            <span className={'fw-semibold'}>
                {value || '—'}
            </span>
        </div>
    );

    return (
        <Modal centered onHide={onHide} show={show} size={'xl'}>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FormattedMessage defaultMessage="Question Preview" id="question_preview.b0996901" />
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className={'g-3'}>
                    <Col md={8} xs={12}>
                        <div className={'ratio ratio-4x3'}>
                            {iframeLoading && (
                                <div className={'w-100 h-100 d-flex align-items-center justify-content-center'}>
                                    <GMetrixLoader size={'large'} />
                                </div>
                            )}
                            <iframe

                                onLoad={() => {

                                    if (loadTimeout.current) clearTimeout(loadTimeout.current);

                                    loadTimeout.current = window.setTimeout(() => {

                                        setState({ iframeLoading: false });

                                    }, 300);

                                }}
                                src={question
                                    ? `https://${baseUrl}/open-question-viewer?QuestionId=${question.QuestionId}&langId=${cultureId}&teacherId=${teacherId}&studentId=${studentId}`
                                    : 'about:blank'}
                                style={{
                                    opacity: iframeLoading ? 0 : 1,
                                    transition: 'opacity 0.2s ease-in-out',
                                    visibility: iframeLoading ? 'hidden' : 'visible'
                                }}
                                title={formatMessage({ id: 'question_preview.b0996901', defaultMessage: 'Question Preview' })}
                            />
                        </div>
                    </Col>
                    <Col md={4} xs={12}>
                        <div className={'d-flex flex-column gap-3'}>
                            {renderMeta(formatMessage({ id: 'qid.58366cbe', defaultMessage: 'QID' }), question ? String(question.QuestionId) : '')}
                            {renderMeta(formatMessage({ id: 'objective.4eee91e9', defaultMessage: 'Objective' }), question?.ObjectiveName ?? '')}
                            {renderMeta(formatMessage({ id: 'question_type.4b121765', defaultMessage: 'Question Type' }), question?.TypeName ?? '')}
                            {renderMeta(formatMessage({ id: 'source.f63dd3d7', defaultMessage: 'Source' }), question?.Sources ?? '')}
                            {renderMeta(formatMessage({ id: 'topic.671ec7b1', defaultMessage: 'Topic' }), question?.TopicName ?? '')}
                        </div>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    className={'me-auto'}
                    onClick={onHide}
                    variant={'secondary'}
                >
                    <FormattedMessage defaultMessage="Close" id="close.99e01fb5" />
                </Button>
                {includeControl && question && (
                    <Button
                        onClick={() => includeControl.onToggle(question)}
                        variant={included ? 'outline-success' : 'outline-primary'}
                    >
                        {included ?
                            (
                                <>
                                    <i className={'fa-solid fa-check me-1'} />
                                    <FormattedMessage defaultMessage="Included" id="included.c6f773de" />
                                </>
                            )
                            :
                            (
                                <>
                                    <i className={'fa-solid fa-plus me-1'} />
                                    <FormattedMessage defaultMessage="Include" id="include.6d2f50c4" />
                                </>
                            )}
                    </Button>
                )}
                <Button
                    disabled={index <= 0}
                    onClick={() => onNavigate(index - 1)}
                    variant={'primary'}
                >
                    <FormattedMessage defaultMessage="Previous" id="previous.f825144b" />
                </Button>
                <Button
                    disabled={index >= questions.length - 1}
                    onClick={() => onNavigate(index + 1)}
                    variant={'primary'}
                >
                    <FormattedMessage defaultMessage="Next" id="next.50b7834e" />
                </Button>
            </Modal.Footer>
        </Modal>
    );

};
